import { categoryOf, ScorecardCategory } from "../taxonomy/category";
import { enforcementOf, EnforcementLevel } from "../taxonomy/enforcement";
import { nonEmptyText } from "../text";

/** A page in the service's own GitHub repo; `path` is appended to the repo URL. @public */
export interface GithubFixPath {
  title: string;
  path: string;
}

/** The fact value that makes a check not apply, and the hint shown instead. @public */
export interface NotApplicableWhen {
  fact: string;
  value: string | number | boolean;
  hint: string;
  /** Chip text; defaults to NOT_APPLICABLE_LABEL. */
  label?: string;
}

/** @public */
export const NOT_APPLICABLE_LABEL = "Not applicable";

/** Front Door check metadata after normalisation. @public */
export interface FrontDoorCheckMetadata {
  category: ScorecardCategory;
  enforcement: EnforcementLevel;
  /** Look the title and status up in the standards registry. */
  standardId?: string;
  /** The control's general fix guidance. */
  howToFix?: string;
  /** Backstage-specific steps, shown after `howToFix`. */
  fixSteps?: string;
  fixUrl?: string;
  /** The control's status, e.g. proposed or active. */
  controlStatus?: string;
  /** The control's parameters, e.g. thresholds. */
  parameters?: Record<string, string | number | boolean>;
  /** How the scorecard measures the check, shown under its description. */
  measurement?: string;
  /** What enforces the check outside Backstage, e.g. `github`. */
  enforcedBy?: string;
  githubFixPath?: GithubFixPath;
  /** Facts outside the rule that explain a failure, read from the latest fact rows. */
  contextFacts?: string[];
  /** Rule facts left out of a failure's evidence once context facts are shown. */
  redundantWithContext?: string[];
  /** Facts shown beside a passing check. */
  passingDetailFacts?: string[];
  notApplicableWhen?: NotApplicableWhen;
}

type Raw = Record<string, unknown>;

const isRecord = (value: unknown): value is Raw =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const factList = (value: unknown): string[] | undefined =>
  Array.isArray(value) && value.every((v) => nonEmptyText(v) === v)
    ? (value as string[])
    : undefined;

const githubFixPath = (value: unknown): GithubFixPath | undefined =>
  isRecord(value) && nonEmptyText(value.title) && typeof value.path === "string"
    ? { title: value.title as string, path: value.path }
    : undefined;

const notApplicableWhen = (value: unknown): NotApplicableWhen | undefined => {
  if (
    !isRecord(value) ||
    !nonEmptyText(value.fact) ||
    !nonEmptyText(value.hint) ||
    !["string", "number", "boolean"].includes(typeof value.value) ||
    (value.label !== undefined && !nonEmptyText(value.label))
  ) {
    return undefined;
  }
  const rule: NotApplicableWhen = {
    fact: value.fact as string,
    value: value.value as NotApplicableWhen["value"],
    hint: value.hint as string,
  };
  if (value.label !== undefined) rule.label = value.label as string;
  return rule;
};

const parameters = (
  value: unknown
): Record<string, string | number | boolean> | undefined =>
  isRecord(value) &&
  Object.values(value).every((v) =>
    ["string", "number", "boolean"].includes(typeof v)
  )
    ? (value as Record<string, string | number | boolean>)
    : undefined;

/** Parsers for the optional fields; a field whose parser returns undefined is dropped. */
const OPTIONAL_FIELDS: {
  [K in Exclude<keyof FrontDoorCheckMetadata, "category" | "enforcement">]: (
    value: unknown
  ) => FrontDoorCheckMetadata[K];
} = {
  standardId: nonEmptyText,
  howToFix: nonEmptyText,
  fixSteps: nonEmptyText,
  fixUrl: nonEmptyText,
  controlStatus: nonEmptyText,
  parameters,
  measurement: nonEmptyText,
  enforcedBy: nonEmptyText,
  githubFixPath,
  contextFacts: factList,
  redundantWithContext: factList,
  passingDetailFacts: factList,
  notApplicableWhen,
};

/** Reads raw check metadata, dropping fields that are malformed. @public */
export function checkMetadataOf(
  raw: Record<string, unknown> | undefined
): FrontDoorCheckMetadata {
  const out: Record<string, unknown> = {
    category: categoryOf(raw?.category),
    enforcement: enforcementOf(raw?.enforcement),
  };
  for (const [key, parse] of Object.entries(OPTIONAL_FIELDS)) {
    const value = (parse as (v: unknown) => unknown)(raw?.[key]);
    if (value !== undefined) out[key] = value;
  }
  return out as unknown as FrontDoorCheckMetadata;
}

/** Every fact the metadata's scorecard fields name, in field order, once. @public */
export function checkMetadataFactIds(
  metadata: FrontDoorCheckMetadata
): string[] {
  return [
    ...new Set([
      ...(metadata.contextFacts ?? []),
      ...(metadata.redundantWithContext ?? []),
      ...(metadata.passingDetailFacts ?? []),
      ...(metadata.notApplicableWhen ? [metadata.notApplicableWhen.fact] : []),
    ]),
  ];
}

/** Check metadata `enforcedBy: github`: GitHub already blocks the failure, for example through branch rules. @public */
export function isEnforcedByGitHub(
  metadata: { enforcedBy?: unknown } | undefined
): boolean {
  const by = metadata?.enforcedBy;
  return typeof by === "string" && by.toLocaleLowerCase("en-US") === "github";
}

/** The not-applicable hint when the check's `notApplicableWhen` fact has its value. @public */
export function notApplicableHint(
  metadata: Pick<FrontDoorCheckMetadata, "notApplicableWhen">,
  readFact: (factId: string) => unknown
): string | undefined {
  const rule = metadata.notApplicableWhen;
  return rule && readFact(rule.fact) === rule.value ? rule.hint : undefined;
}

/** "Proposed", "Draft" and so on for a control that is not active; undefined otherwise. @public */
export function controlStatusLabel(
  metadata: Pick<FrontDoorCheckMetadata, "controlStatus">
): string | undefined {
  const status = metadata.controlStatus;
  if (!status || status === "active") return undefined;
  return status.charAt(0).toLocaleUpperCase("en-US") + status.slice(1);
}

/** The chip text of a not-applicable check. @public */
export function notApplicableLabel(
  metadata: Pick<FrontDoorCheckMetadata, "notApplicableWhen">
): string {
  return metadata.notApplicableWhen?.label ?? NOT_APPLICABLE_LABEL;
}
